'use client'

import { getCurrentUser } from '@lastbrand/fe-common/universal-auth'
import { useCallback } from 'react'

import svdWishlistService from '@src/quince-site-common/src/services/svd-wishlist/svd-wishlist.service'
import {
  addSvdItemToLocalStore,
  isSvdItemInWishlist,
  removeSvdItemFromLocalStore,
  type SvdWishlistEntry,
} from '@src/quince-site-common/src/services/svd-wishlist/svd-wishlist.store'
import { ACTION } from '@src/quince-site-common/src/services/Wishlist.types'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import type { SvdNativeCardVariant } from '@src/types/svd-product-card.types'

import { trackSvdCardEvent } from '../svd-anchor-analytics'

/**
 * SVD wishlist hook (Phase 2) — the SVD-flow analogue of `useProductWishList`, keyed by
 * `productId + selectedAttributes`. The local superset store is the client cache for both guests and
 * logged-in users (read path for the heart's filled state); logged-in writes additionally persist to
 * the `/v2` backend. Guests are localStorage-only (the v2 call is skipped). Backend errors are
 * swallowed so the optimistic local toggle never blocks the UI.
 *
 * Logged-in state is resolved via the async `getCurrentUser()` (restores the session on a cold
 * refresh) rather than the synchronous `AuthUtil.isLoggedIn()`, which is false until the session
 * rehydrates and would skip the backend write. The local store is written optimistically first, so
 * awaiting auth before the backend call doesn't affect the UI; a missed backend write is also
 * recovered by the login-merge on the next wishlist-page load.
 */
export const useSvdWishlist = () => {
  const isItemInWishList = useCallback(
    (productId: string, selectedAttributes: Record<string, string>) =>
      isSvdItemInWishlist(productId, selectedAttributes),
    [],
  )

  const addItemToWishList = useCallback(
    async (entry: SvdWishlistEntry, trackConfig: Record<string, unknown>, variant?: SvdNativeCardVariant) => {
      addSvdItemToLocalStore(entry)
      // Emit via the SVD seam so the anchor VDA keys land in `context` (forwarded by the IA handler).
      trackSvdCardEvent(TELEMETRY_EVENT_NAMES.ADD_TO_WISHLIST, trackConfig, variant)
      if ((await getCurrentUser())?.email) {
        await svdWishlistService.updateWishlist(entry.productId, entry.selectedAttributes, ACTION.ADD)
      }
    },
    [],
  )

  const removeItemFromWishList = useCallback(
    async (
      productId: string,
      selectedAttributes: Record<string, string>,
      trackConfig: Record<string, unknown>,
      variant?: SvdNativeCardVariant,
    ) => {
      removeSvdItemFromLocalStore(productId, selectedAttributes)
      trackSvdCardEvent(TELEMETRY_EVENT_NAMES.REMOVE_FROM_WISHLIST, trackConfig, variant)
      if ((await getCurrentUser())?.email) {
        await svdWishlistService.updateWishlist(productId, selectedAttributes, ACTION.REMOVE)
      }
    },
    [],
  )

  return { isItemInWishList, addItemToWishList, removeItemFromWishList }
}
